import { useT, useLang } from "@/i18n/LanguageContext";
import CountUp from "@/components/CountUp";
import HeroBackgroundSlideshow from "@/components/HeroBackgroundSlideshow";

export default function Hero() {
    const t = useT();
    const { lang } = useLang();
    const h = t.home.hero;
    // CJK characters render visually smaller at the same px-size than Latin letters,
    // so bump the body slightly in zh for visual parity.
    const bodyFontSize = lang === "zh" ? "clamp(19px, 1.55vw, 24px)" : "clamp(18px, 1.4vw, 22px)";

    return (
        <section
            data-testid="hero"
            id="top"
            className="section-x relative"
            style={{
                paddingTop: 0,
                paddingBottom: "clamp(48px, 6vw, 88px)",
                background: "var(--mist)",
            }}
        >
            {/* ─── TOP: animated background — full viewport width ─── */}
            <div
                className="relative"
                style={{
                    width: "100vw",
                    position: "relative",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    paddingLeft: "clamp(20px, 4vw, 40px)",
                    paddingRight: "clamp(20px, 4vw, 40px)",
                    paddingTop: "clamp(56px, 6vw, 88px)",
                    paddingBottom: "clamp(40px, 4vw, 64px)",
                    minHeight: "clamp(360px, 30vw, 440px)",
                    display: "flex",
                    alignItems: "center",
                    background: "var(--navy)",
                    color: "#fff",
                    overflow: "hidden",
                    isolation: "isolate",
                }}
            >
                <HeroBackgroundSlideshow />

                <div
                    className="relative mx-auto w-full"
                    style={{ zIndex: 2, maxWidth: 1200 }}
                >
                    <h1
                        data-testid="hero-headline"
                        className="rise delay-1 font-display"
                        style={{
                            fontSize: "clamp(36px, 5.4vw, 78px)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                            fontWeight: 500,
                            color: "#fff",
                            textShadow: "0 2px 24px rgba(0,0,0,0.35)",
                        }}
                    >
                        {h.line1}<br />
                        <em style={{ fontStyle: "normal", color: "var(--orange)", fontWeight: 500 }}>{h.line2}</em><br />
                        {h.line3}
                    </h1>

                    <p
                        className="rise delay-2 font-mono mt-7"
                        style={{
                            fontSize: 12,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            color: "var(--orange)",
                            fontWeight: 600,
                        }}
                    >
                        {h.tag}
                    </p>
                </div>
            </div>

            {/* ─── BOTTOM: body + outcomes table on the right ─── */}
            <div className="grid grid-cols-12 gap-x-10 mt-12 rise delay-4 rule-top pt-10">
                <div className="col-span-12 lg:col-span-7">
                    <div style={{ maxWidth: 720 }}>
                        {h.body.map((para, i) => (
                            <p
                                key={i}
                                className="font-serif"
                                style={{
                                    fontSize: bodyFontSize,
                                    lineHeight: 1.55,
                                    color: "var(--ink)",
                                    fontWeight: 400,
                                    marginTop: i === 0 ? 0 : 20,
                                }}
                            >
                                {para}
                            </p>
                        ))}
                    </div>

                    <div className="mt-12 flex flex-wrap items-center gap-8">
                        <a href="/contact" data-testid="hero-cta-primary" className="btn-primary">
                            {h.ctaPrimary}
                            <span style={{ fontFamily: "serif", marginLeft: 6 }}>→</span>
                        </a>
                        <p className="font-mono" style={{ fontSize: 12, color: "var(--steel)", lineHeight: 1.6, letterSpacing: "0.04em" }}>
                            {h.ctaSubLine1}<br />
                            {h.ctaSubLine2}
                        </p>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-4 lg:col-start-9 mt-10 lg:mt-0">
                    <div
                        className="px-7 py-7"
                        style={{
                            background: "var(--paper)",
                            border: "1px solid var(--line)",
                        }}
                    >
                        <p className="font-mono" style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--orange)", fontWeight: 500 }}>
                            {h.outcomesLabel}
                        </p>
                        <div className="mt-2 mb-4" style={{ width: 32, height: 2, background: "var(--orange)" }} />
                        <ul>
                            {h.stats.map((s, i, arr) => (
                                <li
                                    key={s.label}
                                    data-testid={`hero-stat-${i}`}
                                    className="flex items-center justify-between gap-4 py-3.5"
                                    style={{ borderBottom: i < arr.length - 1 ? "1px solid var(--line)" : "none" }}
                                >
                                    <span className="font-display tabular" style={{ fontSize: 32, lineHeight: 1, color: "var(--navy)", fontWeight: 600, letterSpacing: "-0.01em" }}>
                                        <CountUp value={s.num} duration={1400 + i * 120} />
                                    </span>
                                    <span className="font-mono text-right" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--steel)", lineHeight: 1.4, flex: "1 1 auto", minWidth: 0, marginLeft: 12 }}>
                                        {s.label}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}
